/**
 * Re-export shim — `filterPaymentTransactions` moved to
 * `@releve/shared/documents/invoice/payments` so the server-side invoice
 * document assembler and the screen narrow payment history through ONE
 * implementation (the PDF is customer-facing; a stale posting listed there as
 * a payment is the worst place for that drift). Existing web importers
 * (useAccountsReceivable / useAccountsPayable / the AR & AP grids / …) keep
 * importing it from here unchanged.
 *
 * `voidedPayments` below deliberately stayed in web: it depends on
 * `transactionTouchesBank` and drives the bill's / invoice's web-only "Voided
 * payments" audit section, not the document.
 */
export { filterPaymentTransactions } from '@releve/shared/documents/invoice/payments';

import { transactionTouchesBank } from '@/lib/bankAccount';

interface JournalWithEntries {
  id: string;
  void_of_transaction_id?: string | null;
  entries?: Array<{
    account?: { account_type?: { type_name?: string | null } | null } | null;
    chart_of_accounts?: { account_type?: { type_name?: string | null } | null } | null;
  }>;
}

/**
 * The undone-payment counterpart to {@link filterPaymentTransactions}: return
 * the reversed *payments* on a bill or invoice, each paired with the reversal
 * that cancels it, for a "Voided payments" audit section. ONE implementation
 * for AP and AR — the only difference is the `requireBankLeg` gate.
 *
 * This is NOT the naive inverse of `filterPaymentTransactions` — that filter
 * also drops force-edit *superseded postings* (case 3), which are voided but
 * are postings, not payments. A superseded posting must not appear as a
 * "voided payment".
 *
 * - `requireBankLeg: true` (AP): the distinguisher is account-category, not
 *   description — an AP payment posts against a feed (bank/cash) account
 *   (`transactionTouchesBank`), whereas a posting only touches expense/AP, so
 *   a voided journal without a bank leg is a superseded posting and skipped.
 * - `requireBankLeg: false` (AR): no bank gate. AR "Deposit to" offers any
 *   account, so a bank gate would hide real reversals. The only AR superseded
 *   postings come from `edit_channel_sale`, and the caller returns `[]` for
 *   channel sales instead of calling this.
 *
 * A pair is `{ payment, reversal }` where `reversal.void_of_transaction_id ===
 * payment.id`. Both journals are otherwise hidden from Payment History, so
 * this MUST run on the RAW transactions, before `filterPaymentTransactions`.
 */
export function voidedPayments<T extends JournalWithEntries>(
  transactions: T[] | null | undefined,
  creatingTransactionId: string | null | undefined,
  { requireBankLeg }: { requireBankLeg: boolean },
): Array<{ payment: T; reversal: T }> {
  if (!transactions) return [];

  const reversalByTarget = new Map<string, T>();
  for (const t of transactions) {
    if (t.void_of_transaction_id) reversalByTarget.set(t.void_of_transaction_id, t);
  }

  const pairs: Array<{ payment: T; reversal: T }> = [];
  for (const p of transactions) {
    if (p.id === creatingTransactionId) continue; // the current posting
    if (p.void_of_transaction_id) continue; // p is itself a reversal, not the reversed thing
    const reversal = reversalByTarget.get(p.id);
    if (!reversal) continue; // p was never voided
    if (requireBankLeg && !transactionTouchesBank(p.entries)) continue; // superseded posting, not a payment
    pairs.push({ payment: p, reversal });
  }
  return pairs;
}

/**
 * Whether ONE payment journal is linked to a bank-feed line. Such a payment is
 * undone from the feed, never reversed or re-pointed from the document, and
 * the undo/repoint RPCs refuse it (`IMPORT_LINKED`). The three link
 * mechanisms the RPCs check:
 *   1. `created_imports` — `accounting_imports.created_transaction_id`
 *      (receive-a-payment / pay-a-bill from the feed; the dominant flow),
 *   2. `payment_import_matches` — `accounting_import_matches.payment_transaction_id`
 *      (a committed split),
 *   3. `matched_imports` — `accounting_import_matches.transaction_id`
 *      (a plain reconciliation match).
 * A field the caller's select does not carry counts as "no link" — the
 * server-side refusal stays the backstop.
 */
export function isFeedLinkedPayment(
  payment:
    | {
        matched_imports?: readonly unknown[] | null;
        created_imports?: readonly unknown[] | null;
        payment_import_matches?: readonly unknown[] | null;
      }
    | null
    | undefined,
): boolean {
  if (!payment) return false;
  return (
    (payment.matched_imports?.length ?? 0) > 0 ||
    (payment.created_imports?.length ?? 0) > 0 ||
    (payment.payment_import_matches?.length ?? 0) > 0
  );
}
